import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { emptyJobForm, serviceError, contactError, scheduleError, toPayload } from "@/lib/jobForm";
import { eur, fmtLongDate, KIND } from "@/lib/format";
import { BrandLogo, StatusBadge } from "@/components/Brand";
import { ServiceStep } from "@/components/booking/ServiceStep";
import { ContactStep } from "@/components/booking/ContactStep";
import { ScheduleStep } from "@/components/booking/ScheduleStep";
import { QuoteCard } from "@/components/booking/QuoteCard";
import { LegalFooter } from "@/components/LegalFooter";

const HERO = "https://images.unsplash.com/photo-1681042803902-f79c240d8f03?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const STEPS = ["Servizio", "Dati e posizione", "Data e ora", "Riepilogo"];

function Stepper({ step }) {
  return (
    <ol className="flex gap-2" data-testid="booking-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex-1">
          <div className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? "bg-[#61a229]" : "bg-slate-200"}`} />
          <p className={`mt-2 hidden sm:block text-xs font-medium ${i === step ? "text-slate-900" : "text-slate-400"}`}>{i + 1}. {s}</p>
        </li>
      ))}
    </ol>
  );
}

function Summary({ form, config }) {
  const eq = config?.equipment.find((e) => e.key === form.equipment_type);
  const rows = [
    ["Servizio", `${KIND[form.kind]}${eq && form.kind !== "sopralluogo" ? ` · ${eq.label}` : ""}`],
    ["Marca / Modello", form.brand_model || (form.photos.length ? `${form.photos.length} foto allegate` : "-")],
    ["Cliente", `${form.first_name} ${form.last_name}`],
    ["Contatti", `${form.phone} · ${form.email}`],
    ["Indirizzo", `${form.address}, ${form.city}`],
    ["Appuntamento", `${fmtLongDate(form.date)} · ${form.start}–${form.end}`],
  ];
  return (
    <dl className="divide-y rounded-2xl border bg-white" data-testid="booking-summary">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-3 gap-4 px-5 py-3.5 text-sm">
          <dt className="text-slate-500">{k}</dt><dd className="col-span-2 font-medium text-slate-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Success({ result, onReset }) {
  return (
    <div className="mx-auto max-w-xl rounded-3xl border bg-white p-8 sm:p-10 text-center rise" data-testid="booking-success">
      <CheckCircle2 className="mx-auto h-14 w-14 text-[#61a229]" />
      <h2 className="mt-5 font-display text-3xl font-bold">Richiesta inviata</h2>
      <p className="mt-3 text-slate-600">Abbiamo ricevuto la tua richiesta per il {fmtLongDate(result.date)} ({result.start}–{result.end}). Ti contatteremo su WhatsApp per la conferma.</p>
      <div className="mt-5 flex justify-center"><StatusBadge status={result.status} testid="booking-success-status" /></div>
      <p className="mt-5 text-sm text-slate-500">Diritto di chiamata: <b className="text-slate-900" data-testid="booking-success-call-fee">{eur(result.quote.call_fee)}</b> + IVA · il resto dipende da tempo e ricambi</p>
      <div className="mt-6 rounded-2xl border border-dashed p-4">
        <p className="text-xs text-slate-500">Il tuo codice prenotazione</p>
        <p className="font-mono text-2xl font-bold tracking-wider" data-testid="booking-success-code">{result.code}</p>
        <p className="mt-1 text-xs text-slate-500">Conservalo: ti serve per cambiare data o annullare fino a 12 ore prima.</p>
        <Link to={`/gestisci?code=${result.code}`} className="mt-2 inline-block text-sm font-semibold text-[#61a229] hover:underline" data-testid="booking-success-manage-link">Gestisci prenotazione</Link>
      </div>
      <Button onClick={onReset} className="mt-8 rounded-full" data-testid="booking-new-button">Nuova richiesta</Button>
    </div>
  );
}

export default function BookingPage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(emptyJobForm());
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [leadToken, setLeadToken] = useState(null);

  useEffect(() => { api.get("/public/config").then((r) => setConfig(r.data)); }, []);

  const saveLead = () => {
    const p = toPayload(form);
    const lead = { token: leadToken, kind: p.kind, equipment_type: p.equipment_type, first_name: p.first_name, last_name: p.last_name,
      email: p.email, phone: p.phone, address: p.address, city: p.city, date: p.date || null, start: p.start || null, end: p.end || null };
    api.post("/public/leads", lead).then((r) => setLeadToken(r.data.token)).catch(() => {});
  };

  const next = () => {
    const err = (step === 1 && !form.privacy_accepted ? "Per proseguire accetta l'informativa privacy" : null) || [serviceError, contactError, scheduleError][step]?.(form);
    if (err) return toast.error(err);
    if (step >= 1) saveLead();
    setStep((s) => s + 1);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/public/bookings", { ...toPayload(form), lead_token: leadToken, privacy_accepted: form.privacy_accepted, marketing_consent: form.marketing_consent });
      setResult(data);
      setLeadToken(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3.5">
          <BrandLogo />
          <div className="flex items-center gap-5">
            <Link to="/gestisci" className="text-sm font-medium text-slate-600 hover:text-[#61a229] transition-colors" data-testid="manage-booking-link">Gestisci prenotazione</Link>
            <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-[#61a229] transition-colors" data-testid="staff-login-link">Area riservata</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden bg-[#0f1710] grain">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-24">
          <p className="rise text-[11px] font-mono uppercase tracking-[0.25em] text-[#a2ca6b]">Termoidraulica · Pellet · Pompe di calore · Solare · Building automation</p>
          <h1 className="rise rise-1 mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
            Richiedi un servizio.
          </h1>
          <p className="rise rise-2 mt-5 max-w-xl text-base text-slate-300">Richiedi un intervento o un sopralluogo, scegli data e fascia oraria.</p>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        {result ? (
          <Success result={result} onReset={() => { setResult(null); setForm(emptyJobForm()); setStep(0); }} />
        ) : (
          <div className="mx-auto max-w-4xl">
            <div className="space-y-8">
              <Stepper step={step} />
              <div className="rise" key={step}>
                <h2 className="mb-6 font-display text-2xl font-bold text-slate-900">{STEPS[step]}</h2>
                {step === 0 && <ServiceStep form={form} set={setForm} config={config} />}
                {step === 1 && <ContactStep form={form} set={setForm} consents />}
                {step === 2 && <ScheduleStep form={form} set={setForm} />}
                {step === 3 && <div className="space-y-6"><Summary form={form} config={config} /><QuoteCard form={form} config={config} /></div>}
              </div>
              <div className="flex items-center justify-between border-t pt-6">
                <Button variant="ghost" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="booking-back-button">
                  <ArrowLeft className="h-4 w-4" /> Indietro
                </Button>
                {step < 3 ? (
                  <Button onClick={next} className="rounded-full px-6" data-testid="booking-next-button">Avanti <ArrowRight className="h-4 w-4" /></Button>
                ) : (
                  <Button onClick={submit} disabled={sending} className="rounded-full px-6" data-testid="booking-submit-button">
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />} Invia richiesta
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
